import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { User, AuthTokens } from '../types'
import { getMe, renewToken, logoutApi, registerRenewHandler } from '../api/auth'
import { getAuthNoticeKeyFromError, persistAuthNotice } from '../utils/authFeedback'

const tokenRenewLeadTimeMs = 5 * 60 * 1000

function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const expiresAt = getTokenExpiryMs(token)
  return expiresAt === null || expiresAt <= Date.now()
}

function isTokenExpiringSoon(token: string): boolean {
  const expiresAt = getTokenExpiryMs(token)
  return expiresAt === null || expiresAt - Date.now() <= tokenRenewLeadTimeMs
}

type AuthContextType = {
  user: User | null
  token: string | null
  loading: boolean
  setAuth: (tokens: AuthTokens, user: User) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// Access token lives in localStorage (persists across tabs and sessions)
// Refresh token lives in an httpOnly cookie set by the backend (not accessible to JS at all)
const SESSION_TOKEN_KEY = 'mac_token'

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const renewPromiseRef = useRef<Promise<string | null> | null>(null)

  const clearLocalSession = useCallback(() => {
    localStorage.removeItem(SESSION_TOKEN_KEY)
    setToken(null)
    setUser(null)
  }, [])

  const clearRemoteSession = useCallback(async () => {
    try {
      await logoutApi()
    } catch {
      // ignore
    }
  }, [])

  const persistAuthNoticeFromError = useCallback((message: string | null | undefined) => {
    const notice = getAuthNoticeKeyFromError(message)
    if (notice) persistAuthNotice(notice)
  }, [])

  const renew = useCallback(async (): Promise<string | null> => {
    if (renewPromiseRef.current) return renewPromiseRef.current

    const renewPromise = (async () => {
      const result = await renewToken()
      if (result.data) {
        const newToken = result.data.token
        localStorage.setItem(SESSION_TOKEN_KEY, newToken)
        setToken(newToken)
        return newToken
      }
      persistAuthNoticeFromError(result.error)
      clearLocalSession()
      await clearRemoteSession()
      return null
    })()

    renewPromiseRef.current = renewPromise
    try {
      return await renewPromise
    } finally {
      renewPromiseRef.current = null
    }
  }, [clearLocalSession, clearRemoteSession, persistAuthNoticeFromError])

  useEffect(() => {
    registerRenewHandler(renew)
  }, [renew])

  useEffect(() => {
    async function init() {
      const storedToken = localStorage.getItem(SESSION_TOKEN_KEY)

      // Token exists and not locally expired — verify with server
      if (storedToken && !isTokenExpired(storedToken)) {
        const result = await getMe(storedToken)
        if (result.data) {
          setToken(storedToken)
          setUser(result.data)
          setLoading(false)
          return
        }
        const notice = getAuthNoticeKeyFromError(result.error)
        if (notice) {
          persistAuthNotice(notice)
          clearLocalSession()
          await clearRemoteSession()
          setLoading(false)
          return
        }
      }

      // Token missing or expired — try to renew via httpOnly cookie
      const newToken = await renew()
      if (newToken) {
        const meResult = await getMe(newToken)
        if (meResult.data) {
          setUser(meResult.data)
          setLoading(false)
          return
        }
        persistAuthNoticeFromError(meResult.error)
        clearLocalSession()
        await clearRemoteSession()
      }

      // No valid session
      clearLocalSession()
      setLoading(false)
    }

    init()
  }, [clearLocalSession, clearRemoteSession, renew, persistAuthNoticeFromError])

  useEffect(() => {
    if (!token) return undefined

    const expiresAt = getTokenExpiryMs(token)
    if (expiresAt === null) {
      void renew()
      return undefined
    }

    const renewDelayMs = Math.max(0, expiresAt - Date.now() - tokenRenewLeadTimeMs)
    const timeoutId = window.setTimeout(() => {
      void renew()
    }, renewDelayMs)

    const handleSessionCheck = () => {
      if (document.visibilityState === 'visible' && isTokenExpiringSoon(token)) {
        void renew()
      }
    }

    window.addEventListener('focus', handleSessionCheck)
    document.addEventListener('visibilitychange', handleSessionCheck)

    return () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener('focus', handleSessionCheck)
      document.removeEventListener('visibilitychange', handleSessionCheck)
    }
  }, [token, renew])

  const setAuth = useCallback((tokens: AuthTokens, userData: User) => {
    localStorage.setItem(SESSION_TOKEN_KEY, tokens.token)
    setToken(tokens.token)
    setUser(userData)
  }, [])

  const logout = useCallback(async () => {
    clearLocalSession()
    // Best-effort: clear the server-side cookie. If this fails the access token
    // will expire naturally, and the refresh token may persist until its TTL.
    // This is an acceptable trade-off for immediate UX.
    await clearRemoteSession()
  }, [clearLocalSession, clearRemoteSession])

  const value = useMemo(
    () => ({ user, token, loading, setAuth, logout }),
    [user, token, loading, setAuth, logout],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
